import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import {APP_INITIALIZER, ErrorHandler, NgModule} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { ServiceWorkerModule } from '@angular/service-worker';

import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { ApiModule, AuthApiService, UsersService, PermissionsService } from '@spa/api';

import { BffAuthService } from './shared/services/bff-auth.service';
import { BffUsersService } from './shared/services/bff-users.service';
import { BffPermissionsService } from './shared/services/bff-permissions.service';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { environment } from 'src/environments/environment';
import { PagesModule } from './pages/pages.module';
import { Language } from './shared/language';
import { AuthGuardService } from './shared/auth-guard.service';
import { AuthInterceptor, CacheInterceptor, ErrorHandlerFactory } from './shared/providers';
import {BuildDetailsHttpService, ClientRedirectGuardService} from './shared/services';
import { ErrorComponent } from './pages/error/error.component';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}

export function fetchBuildDetails(buildDetailsService: BuildDetailsHttpService) {
	return () => buildDetailsService.fetchBuildDetails();
}

@NgModule({
  declarations: [AppComponent, ErrorComponent],
  imports: [
    BrowserModule,
    HttpClientModule,
    AppRoutingModule,
    BrowserAnimationsModule,

    MatButtonModule,
    MatBadgeModule,

    PagesModule,

    TranslateModule.forRoot({
      defaultLanguage: Language.English,
      useDefaultLang: true,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),

    ApiModule.forRoot({
      authUrl: environment.authUrl,
      authId: environment.authId,
      authRedirectUrl: '',
      authScopes: '',
      authRandomGeneratedAwaitLoginKey: '',
      authClientStorageKey: '',
      customCacheResponseHeader: environment.customCacheResponseHeader,
      customProtectedApiHeader: environment.customProtectedApiHeader,
      filesApiUrl: environment.filesApiUrl,
      crmApiUrl: environment.crmApiUrl,
      crmBaseApiUrl: environment.crmBaseApiUrl,
      fundOrdersApiUrl: environment.fundOrdersApiUrl,
      amsApiUrl: environment.amsApiUrl,
      reportingNetBaseApiUrl: environment.reportingNetBaseApiUrl,
      auditApiUrl: '',
      supportSystemApiUrl: '',
      sesApiUrl: '',
      googleDataAnalyzeKey: environment.googleDataAnalyzeKey,
      authDisableSessionMonitoring: true,
    }),

    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.pwa,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
  ],
  providers: [
    AuthGuardService,
    ClientRedirectGuardService,
    /**
     * 'useExisting' keeps the token and 'BffAuthService' on a single instance. 'useClass'
     * constructs a second one, so whoever injects 'BffAuthService' directly gets its own
     * user cache and stops seeing changes made through 'AuthApiService'.
     */
    { provide: AuthApiService, useExisting: BffAuthService },
    { provide: UsersService, useClass: BffUsersService },
    { provide: PermissionsService, useClass: BffPermissionsService },

    {
      provide: Window,
      useValue: window,
    },

    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useFactory: ErrorHandlerFactory
    },
	{
		provide: 'googleTagManagerId',
		useValue: environment.googleDataAnalyzeKey
	},
    {
		  provide: APP_INITIALIZER,
		  useFactory: fetchBuildDetails,
		  deps: [BuildDetailsHttpService],
		  multi: true
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}
